package pl.michal_cyran.brand_hub.components.widgets

import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.transform
import com.varabyte.kobweb.compose.ui.modifiers.transition
import org.jetbrains.compose.web.css.AnimationTimingFunction.Companion.EaseIn
import org.jetbrains.compose.web.css.AnimationTimingFunction.Companion.EaseOut
import org.jetbrains.compose.web.css.CSSpxValue
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s

// ═══════════════════════════════════════════════════════════════
//  REVEAL ANIMATIONS
//  Wzorce wyciągnięte z tutoring/composables (OfferCard, OfferHeading,
//  PropertyCard, TestimonialCard). Użycie: opakuj element w
//  VisibilityAware, a w bloku { isVisible -> ... } dołóż jedną
//  z poniższych metod do modifiera dziecka.
//
//  Wspólny param `delayMs` — licz go z indeksu np. index * 150,
//  albo pomocniczą funkcją staggerDelay() poniżej.
// ═══════════════════════════════════════════════════════════════

/** index * step + offset — do wyliczania opóźnień w listach/gridach. */
fun staggerDelay(index: Int, step: Int, offset: Int = 0): Int =
    index * step + offset

/**
 * Fade + slide w pionie (góra → w dół, do pozycji docelowej).
 * Źródło: OfferHeading (tytuł H3) — distance=12px, duration=0.4s.
 * Też pasuje jako lekki wariant dla nagłówków/opisów sekcji.
 */
fun Modifier.revealFadeSlideUp(
    isVisible: Boolean,
    delayMs: Int = 0,
    distance: CSSpxValue = 16.px,
    durationS: Double = 0.4,
): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .transform { translateY(if (isVisible) 0.px else distance) }
    .transition(
        Transition.of("opacity", durationS.s, EaseOut, delayMs.ms),
        Transition.of("transform", durationS.s, EaseOut, delayMs.ms)
    )

/**
 * Fade + slide w poziomie (z lewej → w miejsce).
 * Źródło: OfferCard (cały kontener) — distance=-30px, duration=0.5s.
 */
fun Modifier.revealFadeSlideX(
    isVisible: Boolean,
    delayMs: Int = 0,
    distance: CSSpxValue = (-30).px,
    durationS: Double = 0.5,
): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .transform { translateX(if (isVisible) 0.px else distance) }
    .transition(
        Transition.of("opacity", durationS.s, EaseOut, delayMs.ms),
        Transition.of("transform", durationS.s, EaseOut, delayMs.ms)
    )

/**
 * Fade + slide w pionie + delikatny scale-in.
 * Źródło: PropertyCard (distance=24px, scale=0.96) i
 * TestimonialCard (distance=30px, scale=0.92) — kontenery kart.
 */
fun Modifier.revealFadeSlideUpScale(
    isVisible: Boolean,
    delayMs: Int = 0,
    distance: CSSpxValue = 24.px,
    fromScale: Double = 0.96,
    durationS: Double = 0.5,
): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .transform {
        translateY(if (isVisible) 0.px else distance)
        scale(if (isVisible) 1.0 else fromScale)
    }
    .transition(
        Transition.of("opacity", durationS.s, EaseOut, delayMs.ms),
        Transition.of("transform", durationS.s, EaseOut, delayMs.ms)
    )

/**
 * Czysty scale-in (bez opacity), EaseIn — pod ikony w kartach,
 * pojawiające się "z odbiciem" tuż po tekście.
 * Źródło: OfferCard i PropertyCard (icon box) — fromScale=0.5, duration=0.4s.
 */
fun Modifier.revealScaleIn(
    isVisible: Boolean,
    delayMs: Int = 0,
    fromScale: Double = 0.5,
    durationS: Double = 0.4,
): Modifier = this
    .transform { scale(if (isVisible) 1.0 else fromScale) }
    .transition(
        Transition.of("transform", durationS.s, EaseIn, delayMs.ms)
    )

/**
 * Fade + scale-in — pod drobne elementy typu gwiazdki ocen, tagi, chipy.
 * Źródło: TestimonialCard (gwiazdki) — fromScale=0.3, duration=0.3s.
 */
fun Modifier.revealFadeScale(
    isVisible: Boolean,
    delayMs: Int = 0,
    fromScale: Double = 0.3,
    durationS: Double = 0.3,
): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .transform { scale(if (isVisible) 1.0 else fromScale) }
    .transition(
        Transition.of("opacity", durationS.s, EaseOut, delayMs.ms),
        Transition.of("transform", durationS.s, EaseIn, delayMs.ms)
    )

/**
 * Fade + scale-in + rotate — pod większe ikony/badge z mocniejszym
 * efektem "wskakiwania" pod kątem.
 * Źródło: OfferHeading (icon box) — fromScale=0.4, fromRotate=-15deg.
 */
fun Modifier.revealFadeScaleRotate(
    isVisible: Boolean,
    delayMs: Int = 0,
    fromScale: Double = 0.4,
    fromRotateDeg: Double = -15.0,
    fadeDurationS: Double = 0.4,
    transformDurationS: Double = 0.5,
): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .transform {
        scale(if (isVisible) 1.0 else fromScale)
        rotate(if (isVisible) 0.deg else fromRotateDeg.deg)
    }
    .transition(
        Transition.of("opacity", fadeDurationS.s, EaseOut, delayMs.ms),
        Transition.of("transform", transformDurationS.s, EaseIn, delayMs.ms)
    )